import { EllipsisVertical, Play, Plus, Power, Tag, Trash2, Zap } from "lucide-react";
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useParams, useSearch } from "wouter";
import { Loader } from "../components/loader/Loader.tsx";
import {
  type SettingsNavGuard,
  useSettingsNavGuard,
  useSettingsUnsavedGuard,
} from "./Settings/settingsNavGuard.tsx";
import {
  cardListClass,
  cardSurfaceClass,
  clickableCardClass,
  galleryCardsClass,
} from "../components/threads/shared.tsx";
import {
  appliedFilterLabel,
  type PanelControlAxes,
  type PanelControlDimension,
  PanelControls,
  type PanelViewMode,
  viewAxis,
} from "./Settings/panelControls.tsx";
import {
  panelDescriptionClass,
  SettingsPanelHeader,
  type SettingsPanelReturn,
} from "./Settings/SettingsPanelHeader.tsx";
import { isInAppPath } from "@/shared/appLink.ts";
import { RoutineStatePanel } from "@/components/routines/RoutineStatePanel.tsx";
import { useGateValue } from "@statsig/react-bindings";
import { useCanvasHeaderActionsSlot } from "@/components/layout/SplitPanel/CanvasPivotPanel.tsx";
import {
  RoutineFormFields,
  RoutinePermissionsFields,
} from "../components/routines/RoutineFormFields.tsx";
import { getTriggerIcon } from "../components/routines/triggerIcon.ts";
import { DigestLabelSelector } from "../components/routines/DigestLabelSelector.tsx";
import { LabelCatalogEditor } from "../components/routines/LabelCatalogEditor.tsx";
import { RoutineSettingsPanel } from "../components/routines/RoutineSettingsPanel.tsx";
import { SystemRoutineFields } from "../components/routines/SystemRoutineFields.tsx";
import { getRoutineCapabilities } from "../components/routines/routineCapabilities.ts";
import { useRoutineToggle } from "../components/routines/useRoutineToggle.ts";
import {
  AreaPills,
  type AreaRoleFilter,
  areaRoleFilterAxes,
  ROUTINES_AREA_ROLE_STORAGE_KEY,
  useAreaRoleFilter,
} from "../components/routines/areaRoleFilter.tsx";
import { useRoutinesV2 } from "../components/routines/useRoutinesV2.ts";
import { AreaRoleSection, sameRoles, knownRoles } from "../components/routines/AreaRoleFields.tsx";
import type { RoutineRoleId } from "../types/routineAreas.ts";
import { areaKeyOf, filterByAreaAndRole } from "@/shared/areaRoleFilter.ts";
import { routinesWithPacks } from "@/shared/packRoutines.ts";
import { usePacks } from "../components/packs/usePacks.ts";
import { Button } from "../components/ui/button.tsx";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu.tsx";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs.tsx";
import { Toggle } from "../components/ui/toggle.tsx";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog.tsx";
import { Separator } from "../components/ui/separator.tsx";
import { confirmationToast, errorToast } from "../components/ui/toast.tsx";
import {
  useSlackTriggerAvailability,
  useRoutine,
  useRoutineMutations,
  useRoutines,
  useRoutineThreads,
} from "../hooks/useRoutines.ts";
import { cn } from "../lib/utils.ts";
import {
  canRunRoutineManually,
  defaultTriggerConfig,
  getTriggerLabel,
  isTriggerConfigComplete,
  matchesRoutineFilter,
  type RoutineFilter,
  routineErrorMessage,
  routineFilterLabels,
} from "@/shared/routines.ts";
import {
  NEW_ROUTINE_DEFAULT_PERMISSIONS,
  type RoutineResp,
  type TriggerConfig,
  type UpdateRoutineReq,
} from "../types/routines.ts";
import {
  formatDateForThreadList,
  formatThreadStatus,
  statusColorClasses,
} from "../components/threads/util.ts";
import { getThreadPagePath } from "../components/threads/util.ts";
import type { ThreadStatus } from "../types/thread.ts";
import { CanvasPivotPanel } from "@/components/layout/SplitPanel/CanvasPivotPanel.tsx";
import { PanelToolbar } from "@/components/layout/SplitPanel/PanelToolbar.tsx";

// The routines flow lives inside the Settings screen (see `views/Settings`), so
// all in-flow navigation is relative to this base rather than a top-level
// `/routines` route.
export const ROUTINES_BASE_PATH = "/settings/routines";

/**
 * What the composer is pre-filled with when a routine is started from here.
 * Ends mid-sentence, with the trailing space, so the cursor lands where the
 * user keeps typing. Kept in step with native's `CREATE_ROUTINE_PROMPT`.
 *
 * Exported so the canvas view seeds the same sentence this list does: one
 * prompt, so the two surfaces cannot start the user off differently.
 */
export const NEW_ROUTINE_PROMPT = "Build me a routine that ";

/**
 * In-tab page layout for the routine detail and create flows: the shared
 * settings header in child-screen mode, which renders the breadcrumb
 * (Routines › name) on desktop and pushes the title into the mobile chrome.
 * The list view uses `SettingsPanelHeader` directly, with its filter and
 * "New routine" action. Any other way out belongs in `returnAction`, where it
 * carries a label.
 */
const RoutinesPanel = ({
  title,
  onBack,
  returnAction,
  actions,
  mobileActions,
  children,
}: {
  title: string;
  onBack: () => void;
  /** Labelled secondary exit, rendered beside the title. @see SettingsPanelReturn */
  returnAction?: SettingsPanelReturn;
  actions?: ReactNode;
  /** Nav-bar version of `actions` for the mobile chrome header. */
  mobileActions?: ReactNode;
  children: ReactNode;
}) => (
  <div className="pb-12">
    <SettingsPanelHeader
      title={title}
      onBack={onBack}
      parentLabel="Routines"
      backStatsigName="routines-panel-back"
      returnAction={returnAction}
      actions={actions}
      mobileActions={mobileActions}
    />
    {children}
  </div>
);

/**
 * Names the way back to whatever linked into this routine (`?from=`).
 *
 * Derived from our own route table rather than taken from the URL: `from` is a
 * same-app path we accept from the query string, so it can name a page that no
 * longer matches the copy — and its text must never reach the DOM.
 */
const returnLabel = (to: string) =>
  to.startsWith("/threads/") ? "Back to thread" : "Back to previous page";

// --- Routine List Row ---

/**
 * One row for both user-owned routines and shipped system routines.
 *
 * The row exposes one inline action — the enable/disable switch (kind-aware:
 * system → preference override, user → regular update). Run and delete live on
 * the detail page, which clicking anywhere else on the row opens.
 *
 * Exported so the picker card renders the same row as this screen rather than a
 * second one that drifts, behavior included: the row always navigates to the
 * routine's page path, and the enclosing boundary decides what that means. Here
 * it is a page navigation; inside a thread the canvas presents the routine
 * instead, so the card never leaves the conversation.
 */
export const RoutineRow = ({
  routine,
  variant = "row",
  onOpen,
}: {
  routine: RoutineResp;
  /**
   * Host-supplied open behavior, mirroring `ContactList`'s `onOpenContact`. The
   * canvas view drills down in place; every other host omits this and keeps the
   * navigation below. The row can't infer which it is in.
   */
  onOpen?: (routineId: string) => void;
  /**
   * "row" is the compact bordered form the in-thread picker keeps (a white
   * card would vanish against the card it sits in). The settings list uses
   * the white-card surface with the thread-card hover reveal in both of its
   * views: "list" keeps the row layout on that surface, "card" is the
   * gallery card.
   */
  variant?: "row" | "list" | "card";
}) => {
  const { setEnabled } = useRoutineToggle(routine);
  const [, setLocation] = useLocation();

  // Always navigate to the unified detail route — the BE resolves either
  // an ent UUID or a system slug at `GET /api/routines/:id`. A system routine
  // uses its stable slug, a user routine its ent id.
  const handleClick = useCallback(() => {
    if (onOpen) {
      onOpen(routine.id);
      return;
    }
    setLocation(`${ROUTINES_BASE_PATH}/${routine.id}`);
  }, [routine.id, setLocation, onOpen]);

  const TriggerIcon = getTriggerIcon(routine.triggerConfig.type);

  // Stop propagation so toggling the switch doesn't bubble into the row's
  // navigation handler.
  const toggle = (
    <div onClick={(e) => e.stopPropagation()}>
      <span data-statsig-name="routines-toggle-enabled">
        {/* `md`: sized to sit level with the status pills, which `default`'s
            44px track overwhelmed and `sm`'s 12px track sat under. */}
        <Toggle size="md" checked={routine.enabled} onCheckedChange={setEnabled} />
      </span>
    </div>
  );

  if (variant === "card") {
    return (
      <div className={cn(clickableCardClass, "flex flex-col")} onClick={handleClick}>
        <div className="flex items-center gap-2">
          <span className="flex-1 min-w-0 font-medium text-sm truncate">{routine.name}</span>
          {toggle}
        </div>
        {routine.description && (
          <div className="mt-1 text-xs text-muted-foreground line-clamp-2">
            {routine.description}
          </div>
        )}
        {/* mt-auto pins the trigger line to the card floor, so a row of cards
            keeps its footers on one line regardless of description length. */}
        <div className="mt-auto flex items-center gap-2 pt-3 text-xs text-muted-foreground">
          <TriggerIcon className="size-3.5" />
          <span>{getTriggerLabel(routine.triggerConfig)}</span>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex items-center gap-4",
        variant === "list"
          ? clickableCardClass
          : "rounded-lg border border-border p-4 hover:bg-muted/50 transition-colors cursor-pointer",
      )}
      onClick={handleClick}
    >
      <div className="flex-1 min-w-0">
        <div className="mb-1">
          <span className="font-medium text-sm truncate">{routine.name}</span>
        </div>
        {routine.description && (
          <div className="mb-1 text-xs text-muted-foreground line-clamp-1">
            {routine.description}
          </div>
        )}
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <TriggerIcon className="size-3.5" />
          <span>{getTriggerLabel(routine.triggerConfig)}</span>
        </div>
      </div>
      {toggle}
    </div>
  );
};

// --- Routine Detail View ---

/**
 * The routine editor: general fields, settings, permissions, and the run /
 * delete / reset actions.
 *
 * Exported so the canvas can present a routine with the same component this
 * screen uses, rather than a reduced copy that drifts from it. `embedded` drops
 * only the in-tab header bar, whose back button and title the canvas card
 * already supplies — everything below it, dialogs included, is shared.
 *
 * `onLeave` is how an embedded host reclaims the transitions that would
 * otherwise navigate to the routines list: going back, and finishing a delete.
 * That path is a real page navigation, so inside a thread it would drop the
 * conversation; the canvas passes a handler that closes the card instead.
 */
export const RoutineDetail = ({
  routineId,
  embedded = false,
  onLeave,
}: {
  routineId: string;
  /** Render without the in-tab header bar, for a host supplying its own chrome. */
  embedded?: boolean;
  /** Leave this routine (back, or a completed delete). Defaults to the routines list. */
  onLeave?: () => void;
}) => {
  const { routine, isLoading } = useRoutine(routineId);
  const slackTriggerAvailability = useSlackTriggerAvailability();
  // Internal staff only. Gates rendering; the endpoint enforces access itself.
  const internalFeatures = useGateValue("internal-features");
  const routinesV2 = useRoutinesV2();
  // Set only when a canvas host is showing this as a drilled-in child; null on
  // the routine's own settings page, where the panel places actions itself.
  const headerActionsSlot = useCanvasHeaderActionsSlot();
  // Threads only exist for user-owned routines (ents). System routines have no
  // thread history yet — skip the query so we don't 404.
  const userRoutineId = routine && routine.kind === "user" ? routine.id : undefined;
  const { threads, isLoading: threadsLoading } = useRoutineThreads(userRoutineId);
  const {
    updateRoutine,
    deleteRoutine,
    runRoutine,
    editSystemRoutine,
    toggleSystemRoutine,
    updateSystemRoutineSettings,
    updateSystemRoutinePermissions,
    updateSystemRoutineSchedule,
  } = useRoutineMutations();
  const [, setLocation] = useLocation();
  // Optional return target (e.g. the thread that linked here). When present,
  // back/cancel returns there instead of the routines list. It arrives in the
  // query string, so it goes through the same open-redirect guard the artifact
  // links and the auth `returnTo` use — a hand-rolled check here had already
  // drifted from that one, missing its backslash case.
  const rawReturnTo = new URLSearchParams(useSearch()).get("from");
  const returnTo = rawReturnTo && isInAppPath(rawReturnTo) ? rawReturnTo : null;
  const [isSaving, setIsSaving] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  // Dirty-nav guard: when the user tries to navigate away with unsaved
  // edits, capture the intended navigation and show a confirmation. The
  // `pendingNav` ref carries the closure that performs the actual nav so
  // we can run it after the user picks Save or Discard.
  const [showDirtyDialog, setShowDirtyDialog] = useState(false);
  const pendingNavRef = useRef<(() => void) | null>(null);

  // Local form state. Note: `enabled` is NOT in here — it's an instant
  // write-through (a preference override for system routines, an ent update for
  // the user's own), never marks the form dirty, and never goes through Save.
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [prompt, setPrompt] = useState("");
  const [amendment, setAmendment] = useState("");
  const [triggerConfig, setTriggerConfig] = useState<TriggerConfig>({
    type: "cron",
    schedule: "0 9 * * 1-5",
  });
  const [endsAt, setEndsAt] = useState<string | undefined>(undefined);
  const [autoSendCommunication, setAutoSendCommunication] = useState(false);
  const [autoArchive, setAutoArchive] = useState(false);
  const [defaultFrom, setDefaultFrom] = useState<"user" | "assistant">("assistant");
  const [area, setArea] = useState<string | undefined>(undefined);
  const [roles, setRoles] = useState<RoutineRoleId[]>([]);
  const [formReady, setFormReady] = useState(false);
  const [selectedTab, setSelectedTab] = useState("general");

  // The routine id we last seeded the form from, and whether the user has made
  // unsaved edits since. Every Settings-tab write calls `invalidateQueries`,
  // which refetches this routine; without these guards that refetch would
  // re-seed the form and silently clobber in-progress edits in the General tab.
  const seededRoutineIdRef = useRef<string | null>(null);
  const userEditedRef = useRef(false);
  const seedForm = useCallback((r: RoutineResp) => {
    setName(r.name);
    setDescription(r.description ?? "");
    setPrompt(r.prompt);
    setAmendment(r.amendment ?? "");
    setTriggerConfig(r.triggerConfig);
    setEndsAt(r.endsAt);
    setAutoSendCommunication(r.permissions?.autoSendCommunication ?? false);
    setAutoArchive(r.permissions?.autoArchive ?? false);
    setDefaultFrom(r.permissions?.defaultFrom ?? "assistant");
    setArea(r.area);
    setRoles(knownRoles(r.roles));
  }, []);

  // Seed on first load and when switching to a different routine. For a refetch
  // of the SAME routine, only re-seed when there are no unsaved edits — note we
  // track edits explicitly (not via `isDirty`), because a settings write
  // re-renders the server prompt and would otherwise look like a dirty edit.
  useEffect(() => {
    if (!routine) return;
    const sameRoutine = seededRoutineIdRef.current === routine.id;
    if (sameRoutine && userEditedRef.current) return;
    seededRoutineIdRef.current = routine.id;
    userEditedRef.current = false;
    seedForm(routine);
    setFormReady(true);
  }, [routine, seedForm]);

  // Setter wrappers that flag the form as user-edited so a background refetch
  // won't reset it (see the seed effect above).
  // A system routine's permissions and cron schedule write through instantly
  // (no Save), mirroring the Enabled toggle and the Settings tab; its prompt
  // belongs to okthx and the user's changes go in the amendment. A routine the
  // user wrote owns every field and batches through Save → /update.
  const isSystemRoutine = routine?.kind === "system";
  const systemId = routine?.id;

  const handleNameChange = useCallback((v: string) => {
    userEditedRef.current = true;
    setName(v);
  }, []);
  const handlePromptChange = useCallback((v: string) => {
    userEditedRef.current = true;
    setPrompt(v);
  }, []);
  const handleEndsAtChange = useCallback((v: string | undefined) => {
    userEditedRef.current = true;
    setEndsAt(v);
  }, []);
  const handleAmendmentChange = useCallback((v: string) => {
    userEditedRef.current = true;
    setAmendment(v);
  }, []);

  // Only a routine the user wrote has an editable description; on one okthx
  // ships it is read-only copy. So this is an ordinary field that batches
  // through Save, and the ref keeps a background refetch from repainting it
  // mid-edit (see the seed effect above).
  const handleDescriptionChange = useCallback((v: string) => {
    userEditedRef.current = true;
    setDescription(v);
  }, []);
  // Only on a routine the user wrote, under routines_v2: a shipped routine's
  // area and roles are okthx's. Batched through Save like the description.
  const handleAreaChange = useCallback((v: string) => {
    userEditedRef.current = true;
    setArea(v);
  }, []);
  const handleRolesChange = useCallback((v: RoutineRoleId[]) => {
    userEditedRef.current = true;
    setRoles(v);
  }, []);

  // Instant write-through of the full permissions triple for a system
  // routine. Optimistic; reverts to the server value on failure (like
  // RoutineSettingsPanel). Does not mark the form dirty.
  const writeSystemPermissions = useCallback(
    async (next: {
      autoSendCommunication: boolean;
      autoArchive: boolean;
      defaultFrom: "user" | "assistant";
    }) => {
      if (!routine || !systemId) return;
      setAutoSendCommunication(next.autoSendCommunication);
      setAutoArchive(next.autoArchive);
      setDefaultFrom(next.defaultFrom);
      try {
        await updateSystemRoutinePermissions(systemId, next);
      } catch {
        errorToast("Failed to update routine");
        setAutoSendCommunication(routine.permissions?.autoSendCommunication ?? false);
        setAutoArchive(routine.permissions?.autoArchive ?? false);
        setDefaultFrom(routine.permissions?.defaultFrom ?? "assistant");
      }
    },
    [routine, systemId, updateSystemRoutinePermissions],
  );

  const handleTriggerConfigChange = useCallback(
    (v: TriggerConfig) => {
      // Schedule-only edit of a system cron routine → instant override write.
      // A trigger-type change comes through
      // handleTriggerTypeChange instead.
      if (isSystemRoutine && v.type === "cron" && triggerConfig.type === "cron" && systemId) {
        // Only `schedule` is persisted by this fast path, so mirror that in the
        // optimistic update rather than swallowing the full incoming config —
        // any other cron field would silently revert on the next refetch.
        setTriggerConfig({ ...triggerConfig, schedule: v.schedule });
        void (async () => {
          try {
            await updateSystemRoutineSchedule(systemId, v.schedule);
          } catch {
            errorToast("Failed to update routine");
            if (routine) setTriggerConfig(routine.triggerConfig);
          }
        })();
        return;
      }
      userEditedRef.current = true;
      setTriggerConfig(v);
    },
    [
      isSystemRoutine,
      triggerConfig.type,
      systemId,
      updateSystemRoutineSchedule,
      routine,
      triggerConfig,
    ],
  );

  const handleAutoSendCommunicationChange = useCallback(
    (v: boolean) => {
      if (isSystemRoutine) {
        void writeSystemPermissions({
          autoSendCommunication: v,
          autoArchive,
          defaultFrom,
        });
        return;
      }
      userEditedRef.current = true;
      setAutoSendCommunication(v);
    },
    [isSystemRoutine, writeSystemPermissions, autoArchive, defaultFrom],
  );
  const handleAutoArchiveChange = useCallback(
    (v: boolean) => {
      if (isSystemRoutine) {
        void writeSystemPermissions({
          autoSendCommunication,
          autoArchive: v,
          defaultFrom,
        });
        return;
      }
      userEditedRef.current = true;
      setAutoArchive(v);
    },
    [isSystemRoutine, writeSystemPermissions, autoSendCommunication, defaultFrom],
  );
  const handleDefaultFromChange = useCallback(
    (v: "user" | "assistant") => {
      if (isSystemRoutine) {
        void writeSystemPermissions({
          autoSendCommunication,
          autoArchive,
          defaultFrom: v,
        });
        return;
      }
      userEditedRef.current = true;
      setDefaultFrom(v);
    },
    [isSystemRoutine, writeSystemPermissions, autoSendCommunication, autoArchive],
  );

  // Dirty detection — the fields that go through Save. For a system routine,
  // permissions and a cron-schedule edit write through instantly (above), so
  // they never count as dirty; the name, description and prompt are okthx's and
  // not editable at all; what remains is the amendment and non-cron trigger
  // fields.
  const isDirty = useMemo(() => {
    if (!routine) return false;
    if (name !== routine.name) return true;
    // Safe to check unconditionally: a routine okthx ships never has an end
    // time on either side, so this can only fire on one the user owns.
    if (endsAt !== routine.endsAt) return true;
    // Only for a routine the user wrote. On a routine okthx ships the
    // description is read-only, so it can never differ — and counting it would
    // only risk lighting Save over a field the user cannot change.
    if (!isSystemRoutine && description !== (routine.description ?? "")) return true;
    // The area by its key, so "catering" over "Catering" is the same area; the
    // roles as the list still has them, so a stored role that left the list
    // does not open the form dirty.
    if (
      !isSystemRoutine &&
      (areaKeyOf(area) !== areaKeyOf(routine.area) || !sameRoles(roles, knownRoles(routine.roles)))
    ) {
      return true;
    }

    const permsDirty =
      autoSendCommunication !== (routine.permissions?.autoSendCommunication ?? false) ||
      autoArchive !== (routine.permissions?.autoArchive ?? false) ||
      defaultFrom !== (routine.permissions?.defaultFrom ?? "assistant");
    const triggerDirty = JSON.stringify(triggerConfig) !== JSON.stringify(routine.triggerConfig);

    if (!isSystemRoutine) return prompt !== routine.prompt || permsDirty || triggerDirty;

    if (amendment !== (routine.amendment ?? "")) return true;
    // Permissions and a cron schedule are instant; only a non-cron trigger-field
    // edit still needs Save. A trigger-type change is refused by the server —
    // that is a different routine, not a changed one.
    return triggerDirty && triggerConfig.type !== "cron";
  }, [
    routine,
    name,
    description,
    prompt,
    endsAt,
    amendment,
    autoSendCommunication,
    autoArchive,
    defaultFrom,
    triggerConfig,
    isSystemRoutine,
    area,
    roles,
  ]);

  // Instant write-through for the enabled switch. A system routine goes to the
  // toggle endpoint (preferences); a user routine patches its ent's `enabled`
  // directly. Never marks dirty; the user doesn't need to hit Save.
  const handleEnabledChange = useCallback(
    async (next: boolean) => {
      if (!routine) return;
      try {
        if (routine.kind === "system") {
          await toggleSystemRoutine(routine.id, next);
        } else {
          await updateRoutine(routine.id, { enabled: next });
        }
      } catch (err) {
        errorToast(routineErrorMessage(err));
      }
    },
    [routine, toggleSystemRoutine, updateRoutine],
  );

  // Persist edits. `/edit` on a system routine writes preference overrides —
  // its prompt is okthx's and is not sent. `/update` on a user routine writes
  // the ent, prompt included.
  const doSave = useCallback(async (): Promise<
    { ok: true; updated: RoutineResp | null } | { ok: false }
  > => {
    if (!routine || !isDirty) return { ok: true, updated: null };
    if (!isTriggerConfigComplete(triggerConfig)) {
      errorToast("Pick a Slack channel before saving");
      return { ok: false };
    }
    setIsSaving(true);
    try {
      const patch: UpdateRoutineReq = {
        name,
        // Trimmed but NOT collapsed to undefined: JSON drops an undefined key,
        // so the server would see no description at all and keep the old one.
        // The server turns "" into undefined on the way to storage.
        description: description.trim(),
        triggerConfig,
        enabled: routine.enabled,
        // Explicit `null` rather than omitted: the form is authoritative for
        // content on save, so a cleared end time has to reach the server as a
        // clear instruction and not as "leave whatever is stored".
        endsAt: endsAt ?? null,
        permissions: { autoSendCommunication, autoArchive, defaultFrom },
        ...(isSystemRoutine ? { amendment } : { prompt }),
        // Like every field above: the form is authoritative on save, so a save
        // sends the area and roles it shows. Only roles the list still has, as
        // the api refuses one it does not know. No area when the routine has
        // none: the form cannot clear one.
        ...(isSystemRoutine ? {} : { roles, ...(area !== undefined ? { area } : {}) }),
      };
      const updated =
        routine.kind === "system"
          ? await editSystemRoutine(routine.id, patch)
          : await updateRoutine(routine.id, patch);
      // Edits are persisted; let the post-save refetch re-seed the form.
      userEditedRef.current = false;
      return { ok: true, updated };
    } catch (err) {
      // The save path is where an ended routine is refused, and that refusal
      // names the date the user has to change — a generic message strands them.
      errorToast(routineErrorMessage(err, "Failed to save routine"));
      return { ok: false };
    } finally {
      setIsSaving(false);
    }
  }, [
    routine,
    isDirty,
    name,
    description,
    prompt,
    amendment,
    isSystemRoutine,
    triggerConfig,
    endsAt,
    updateRoutine,
    editSystemRoutine,
    autoSendCommunication,
    autoArchive,
    defaultFrom,
    roles,
    area,
  ]);

  const handleSave = useCallback(async () => {
    await doSave();
  }, [doSave]);

  const handleDiscard = useCallback(() => {
    if (!routine) return;
    userEditedRef.current = false;
    seedForm(routine);
  }, [routine, seedForm]);

  // Browser-level guard: any tab close / hard refresh while dirty triggers
  // the platform's built-in "leave site?" dialog. The exact copy is browser-
  // controlled — we just need the listener to be attached for it to show.
  useEffect(() => {
    if (!isDirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = ""; // legacy Chromium
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [isDirty]);

  // In-app guard for the back button: stash the navigation the user wanted
  // and pop the dirty dialog. The dialog's handlers decide whether to fire
  // the nav after a Save / Discard, or drop it on Cancel.
  const guardNavigation = useCallback(
    (proceed: () => void): boolean => {
      if (isDirty) {
        pendingNavRef.current = proceed;
        setShowDirtyDialog(true);
        return false;
      }
      return true;
    },
    [isDirty],
  );

  // Register the dirty guard with the Settings shell so switching to a different
  // Settings tab while this routine has unsaved edits routes through the same
  // Save/Discard dialog instead of silently dropping them. Unlike
  // `guardNavigation`, the Settings guard owns performing the navigation, so the
  // adapter calls `proceed()` itself on the clean path.
  const settingsNavGuard = useSettingsNavGuard();
  const guardTabChange = useCallback<SettingsNavGuard>(
    (proceed) => {
      if (guardNavigation(proceed)) proceed();
    },
    [guardNavigation],
  );
  useEffect(() => {
    settingsNavGuard?.setGuard(guardTabChange);
    return () => settingsNavGuard?.setGuard(null);
  }, [settingsNavGuard, guardTabChange]);

  // Back returns to the routines list (staying inside the Settings tab), guarded
  // by unsaved changes. `guardNavigation` performs the nav on the dirty path via
  // the dialog; on the clean path we navigate directly. It replaces rather than
  // pushes so the editor's history entry collapses — pushing would leave the
  // editor one browser-back away, and the header's back button would re-enter it.
  // It goes to the list unconditionally, even when there is a `returnTo` to
  // offer: see `handleReturn` for why that exit is a labelled link instead.
  const handleBack = useCallback((): void => {
    const goToList = () => {
      if (onLeave) return onLeave();
      setLocation(ROUTINES_BASE_PATH, { replace: true });
    };
    if (guardNavigation(goToList)) goToList();
  }, [guardNavigation, setLocation, onLeave]);

  // The `returnTo` exit (e.g. back to the thread whose "Edit routine" linked
  // here). Replaces for the same reason as `handleBack` — once the user has left
  // the editor, browser-back should skip it rather than re-enter it.
  const handleReturn = useCallback((): void => {
    if (!returnTo) return;
    const goToReturn = () => setLocation(returnTo, { replace: true });
    if (guardNavigation(goToReturn)) goToReturn();
  }, [guardNavigation, setLocation, returnTo]);

  const handleDirtySave = useCallback(async () => {
    const result = await doSave();
    if (!result.ok) return; // toast already shown; leave dialog open
    setShowDirtyDialog(false);
    // Finish the navigation the user started — they chose to leave despite the
    // unsaved changes.
    const nav = pendingNavRef.current;
    pendingNavRef.current = null;
    nav?.();
  }, [doSave]);

  const handleDirtyDiscard = useCallback(() => {
    handleDiscard();
    setShowDirtyDialog(false);
    const nav = pendingNavRef.current;
    pendingNavRef.current = null;
    nav?.();
  }, [handleDiscard]);

  const handleDirtyCancel = useCallback(() => {
    pendingNavRef.current = null;
    setShowDirtyDialog(false);
  }, []);

  const handleTriggerTypeChange = useCallback(
    (newType: TriggerConfig["type"]) => {
      if (newType === triggerConfig.type) return;
      userEditedRef.current = true;
      setTriggerConfig(defaultTriggerConfig(newType));
    },
    [triggerConfig.type],
  );

  const handleRun = useCallback(async () => {
    if (!routine) return;
    setIsRunning(true);
    try {
      await runRoutine(routine.id);
      confirmationToast(`Routine "${routine.name}" triggered`);
    } catch {
      errorToast("Failed to run routine");
    } finally {
      setIsRunning(false);
    }
  }, [routine, runRoutine]);

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const handleDelete = useCallback(async () => {
    if (!routine) return;
    setIsDeleting(true);
    try {
      await deleteRoutine(routine.id);
      if (onLeave) {
        onLeave();
      } else {
        // Replace — the routine is gone, so its entry must not stay reachable
        // by pressing back.
        setLocation(ROUTINES_BASE_PATH, { replace: true });
      }
    } catch {
      errorToast("Failed to delete routine");
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  }, [routine, deleteRoutine, setLocation, onLeave]);

  const returnAction: SettingsPanelReturn | undefined = returnTo
    ? { label: returnLabel(returnTo), onClick: handleReturn, statsigName: "routines-panel-return" }
    : undefined;

  // Action cluster for the in-tab header bar (Save/Discard when dirty, the
  // kind-dependent destructive action, and Run).
  const detailActions = routine ? (
    <>
      {/* Discard + Save show up here when there are unsaved changes,
          so the user doesn't have to scroll past the form to commit. */}
      {isDirty && (
        <>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDiscard}
            data-statsig-name="routine-detail-discard"
          >
            Discard
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSave}
            disabled={isSaving || !isTriggerConfigComplete(triggerConfig)}
            data-statsig-name="routine-detail-save"
          >
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </>
      )}
      {/* A user routine can be deleted. A system routine is okthx's — turning it
          off is the way to stop it, so there is nothing destructive to offer. */}
      {routine.kind === "user" ? (
        <Button
          variant="destructive"
          size="sm"
          onClick={() => setShowDeleteDialog(true)}
          disabled={isDeleting}
          data-statsig-name="routine-detail-delete"
        >
          <Trash2 className="size-4" />
          {isDeleting ? "Deleting..." : "Delete"}
        </Button>
      ) : null}
      {/* Offered only for scheduled routines — `canRunRoutineManually` explains
          why a manual run of a triggered routine isn't meaningful. Where it is
          offered it works for both kinds: user routines fire as their own ent;
          system routines fire via the `system:<id>` sentinel that the backend
          run handler resolves through the registry. */}
      {canRunRoutineManually(routine.triggerConfig) && (
        <Button
          variant="primary"
          size="sm"
          onClick={handleRun}
          disabled={isRunning}
          data-statsig-name="routine-detail-run"
        >
          <Play className="size-4" />
          {isRunning ? "Running..." : "Run"}
        </Button>
      )}
    </>
  ) : null;

  // The mobile nav bar can't fit the full cluster next to a title, so it swaps
  // contextually the way a native edit mode does: committing the edit while
  // dirty (Discard/Save), otherwise Run plus an overflow menu for Delete.
  let mobileDetailActions: ReactNode = null;
  if (routine && isDirty) {
    mobileDetailActions = (
      <>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleDiscard}
          data-statsig-name="routine-detail-discard"
        >
          Discard
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={handleSave}
          disabled={isSaving || !isTriggerConfigComplete(triggerConfig)}
          data-statsig-name="routine-detail-save"
        >
          {isSaving ? "Saving..." : "Save"}
        </Button>
      </>
    );
  } else if (routine) {
    mobileDetailActions = (
      <>
        {canRunRoutineManually(routine.triggerConfig) && (
          <Button
            variant="ghost"
            size="icon"
            className="shrink-0"
            aria-label={isRunning ? "Running routine" : "Run routine"}
            onClick={handleRun}
            disabled={isRunning}
            data-statsig-name="routine-detail-run"
          >
            <Play className="size-5" />
          </Button>
        )}
        {routine.kind === "user" && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="shrink-0"
                aria-label="More actions"
                data-statsig-name="routine-detail-overflow"
              >
                <EllipsisVertical className="size-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                disabled={isDeleting}
                onClick={() => setShowDeleteDialog(true)}
                data-statsig-name="routine-detail-delete"
              >
                <Trash2 className="size-4" />
                {isDeleting ? "Deleting..." : "Delete routine"}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </>
    );
  }

  // Loading and not-found render before there is a name to title with, so they
  // get the placeholder panel on the page — and no panel at all when embedded,
  // where the host is already showing its own header.
  const preContent = (body: ReactNode) =>
    embedded ? (
      body
    ) : (
      <RoutinesPanel title="Routine" onBack={handleBack}>
        {body}
      </RoutinesPanel>
    );

  if (isLoading) {
    return preContent(
      <div className="flex items-center justify-center h-64">
        <Loader />
      </div>,
    );
  }

  if (!routine) {
    return preContent(
      <div className="text-center py-12 text-muted-foreground">Routine not found.</div>,
    );
  }

  // The detail view is tabbed: General (name / when / then / status / threads —
  // or, for a routine okthx ships, the same tab stating what it can't author),
  // Settings — rendered only when the routine has something to configure — and
  // Permissions.
  const { isLabelRoutine, isDigestRoutine, hasDeclaredSettings, hasSettingsTab, statesTrigger } =
    getRoutineCapabilities(routine);
  // Radix keeps its own tab selection, so a tab that stops existing between
  // renders would leave an empty panel with nothing active. Fall back to General
  // whenever the selection no longer exists.
  const activeTab = selectedTab === "settings" && !hasSettingsTab ? "general" : selectedTab;

  const generalSections = (
    <>
      {formReady &&
        (statesTrigger ? (
          <SystemRoutineFields
            description={description}
            triggerConfig={triggerConfig}
            onTriggerConfigChange={handleTriggerConfigChange}
            amendment={amendment}
            onAmendmentChange={handleAmendmentChange}
          />
        ) : (
          <RoutineFormFields
            name={name}
            onNameChange={handleNameChange}
            description={description}
            onDescriptionChange={handleDescriptionChange}
            prompt={prompt}
            onPromptChange={handlePromptChange}
            amendment={amendment}
            onAmendmentChange={handleAmendmentChange}
            isSystemRoutine={isSystemRoutine}
            triggerConfig={triggerConfig}
            onTriggerConfigChange={handleTriggerConfigChange}
            onTriggerTypeChange={handleTriggerTypeChange}
            slackTriggerAvailability={slackTriggerAvailability}
            endsAt={endsAt}
            onEndsAtChange={handleEndsAtChange}
            autoSendCommunication={autoSendCommunication}
            onAutoSendCommunicationChange={handleAutoSendCommunicationChange}
            autoArchive={autoArchive}
            onAutoArchiveChange={handleAutoArchiveChange}
            defaultFrom={defaultFrom}
            onDefaultFromChange={handleDefaultFromChange}
            statsigPrefix="routine-detail"
            showPermissions={false}
            showEndTime={!isSystemRoutine}
            // Never on the detail page: the panel header states the name right
            // above this tab, so a Name field only said it twice. The create
            // form still shows one — there is no header to read it from yet.
            showName={false}
          />
        ))}

      {formReady && routinesV2.enabled && !isSystemRoutine && (
        <>
          <div className="h-px bg-border" />
          <AreaRoleSection
            area={area}
            onAreaChange={handleAreaChange}
            roles={roles}
            onRolesChange={handleRolesChange}
            statsigPrefix="routine-detail"
          />
        </>
      )}

      {/* Internal-only: the actor's scratch notes for this routine. The gate
          decides what renders; the endpoint checks admin/employee itself. */}
      {internalFeatures && (
        <RoutineStatePanel key={routine.id} statePath={`/api/a/routines/${routine.id}/state`} />
      )}

      {/* Enabled toggle */}
      <div className="h-px bg-border" />
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Power className="size-3.5 text-muted-foreground" />
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Status
          </span>
        </div>
        <div className={cn(cardSurfaceClass, "flex w-full items-center justify-between")}>
          <div className="text-left">
            <div className="text-sm font-medium">{routine.enabled ? "Enabled" : "Disabled"}</div>
            <div className="text-xs text-muted-foreground mt-0.5">
              {routine.enabled
                ? "This routine is active and will run when triggered."
                : "This routine is paused and will not run."}
            </div>
          </div>
          <Toggle checked={routine.enabled} onCheckedChange={handleEnabledChange} />
        </div>
      </div>

      {/* Threads */}
      <div className="h-px bg-border" />
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Zap className="size-3.5 text-muted-foreground" />
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Threads
          </span>
        </div>
        {threadsLoading ? (
          <div className="flex items-center justify-center h-16">
            <Loader />
          </div>
        ) : threads.length === 0 ? (
          <div className="text-sm text-muted-foreground py-4 text-center">
            No threads yet. Run this routine to create one.
          </div>
        ) : (
          <div className={cardListClass}>
            {threads.map((thread) => (
              <div
                key={thread.id}
                className={cn(clickableCardClass, "flex items-center gap-3")}
                onClick={() => setLocation(getThreadPagePath(thread.id))}
              >
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-medium truncate block">{thread.title}</span>
                </div>
                <span
                  className={cn(
                    "text-xs capitalize",
                    statusColorClasses[thread.status as ThreadStatus] ?? "",
                  )}
                >
                  {formatThreadStatus(thread.status as ThreadStatus)}
                </span>
                <span className="text-xs text-muted-foreground whitespace-nowrap">
                  {formatDateForThreadList(new Date(thread.createdAt))}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );

  const permissionsSection = formReady && (
    <RoutinePermissionsFields
      autoSendCommunication={autoSendCommunication}
      onAutoSendCommunicationChange={handleAutoSendCommunicationChange}
      autoArchive={autoArchive}
      onAutoArchiveChange={handleAutoArchiveChange}
      defaultFrom={defaultFrom}
      onDefaultFromChange={handleDefaultFromChange}
      statsigPrefix="routine-detail"
    />
  );

  // Auto Inbox is an umbrella routine, not a when/then routine: it has no prompt,
  // trigger, or permissions. `enabled` is the master on/off switch; each
  // capability (today just Labeling) toggles independently beneath it.
  const labelRoutineSections = (
    <div className="space-y-6">
      <div className={cn(cardSurfaceClass, "text-sm text-muted-foreground")}>
        Auto Inbox keeps your mail organized. Classification always runs in Okthx; turn on the
        capabilities below to act on it in your Gmail or Outlook mailbox.
      </div>
      <div>
        <div className="mb-3 flex items-center gap-2">
          <Power className="size-3.5 text-muted-foreground" />
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Status
          </span>
        </div>
        <div className={cn(cardSurfaceClass, "flex w-full items-center justify-between")}>
          <div className="text-left">
            <div className="text-sm font-medium">{routine.enabled ? "Enabled" : "Disabled"}</div>
            <div className="mt-0.5 text-xs text-muted-foreground">
              {routine.enabled
                ? "Auto Inbox is active. Manage its capabilities below."
                : "Auto Inbox is paused. Nothing is applied to your mailbox."}
            </div>
          </div>
          <Toggle checked={routine.enabled} onCheckedChange={handleEnabledChange} />
        </div>
      </div>
      <div className="h-px bg-border" />
      <div>
        <div className="mb-3 flex items-center gap-2">
          <Tag className="size-3.5 text-muted-foreground" />
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Labeling
          </span>
        </div>
        <LabelCatalogEditor systemId={routine.id} masterEnabled={routine.enabled} />
      </div>
    </div>
  );

  // The page constrains its measure against a full-width tab body; a card is
  // already narrow, so the same cap there would waste most of it.
  const detailBody = (
    <div className={cn("space-y-6", !embedded && "max-w-2xl")}>
      {isLabelRoutine ? (
        labelRoutineSections
      ) : (
        <Tabs value={activeTab} onValueChange={setSelectedTab} className="space-y-6">
          <TabsList variant="underline">
            <TabsTrigger variant="underline" value="general">
              General
            </TabsTrigger>
            {hasSettingsTab && (
              <TabsTrigger variant="underline" value="settings">
                Settings
              </TabsTrigger>
            )}
            <TabsTrigger variant="underline" value="permissions">
              Permissions
            </TabsTrigger>
          </TabsList>
          <TabsContent value="general" className="space-y-6">
            {generalSections}
          </TabsContent>
          {hasSettingsTab && (
            <TabsContent value="settings" className="space-y-6">
              {isDigestRoutine && <DigestLabelSelector systemId={routine.id} />}
              {hasDeclaredSettings && (
                <RoutineSettingsPanel
                  settings={routine.settings ?? []}
                  values={routine.settingValues ?? {}}
                  onWrite={async (patch) => {
                    await updateSystemRoutineSettings(routine.id, patch);
                  }}
                />
              )}
            </TabsContent>
          )}
          <TabsContent value="permissions">{permissionsSection}</TabsContent>
        </Tabs>
      )}
    </div>
  );

  return (
    <>
      {detailActions && headerActionsSlot ? createPortal(detailActions, headerActionsSlot) : null}
      {embedded ? (
        <div className="space-y-6">
          {/* The host's chrome carries the title and the way back. Its actions
              go up there too when the host offers a slot, so a drilled-in
              routine's controls sit with the rest of the header chrome rather
              than in a band of their own above the body. Without a slot they
              stay right-aligned here, as they are on the page. */}
          {detailActions && !headerActionsSlot ? (
            <div className="flex items-center justify-end gap-2">{detailActions}</div>
          ) : null}
          {detailBody}
        </div>
      ) : (
        <RoutinesPanel
          title={routine.name}
          onBack={handleBack}
          returnAction={returnAction}
          actions={detailActions}
          mobileActions={mobileDetailActions}
        >
          {detailBody}
        </RoutinesPanel>
      )}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete this routine</DialogTitle>
          </DialogHeader>
          <Separator />
          <DialogDescription>
            This will permanently delete &ldquo;{routine?.name}&rdquo;. This action cannot be
            undone.
          </DialogDescription>
          <DialogFooter>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={isDeleting}
              data-statsig-name="routine-detail-delete-confirm"
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowDeleteDialog(false)}
              data-statsig-name="routine-detail-delete-cancel"
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={showDirtyDialog} onOpenChange={(open) => !open && handleDirtyCancel()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save changes before leaving?</DialogTitle>
          </DialogHeader>
          <Separator />
          <DialogDescription>
            You have unsaved changes to &ldquo;{routine?.name}&rdquo;. Save them, discard them, or
            stay on this page.
          </DialogDescription>
          <DialogFooter>
            <Button
              variant="primary"
              onClick={handleDirtySave}
              disabled={isSaving}
              data-statsig-name="routine-detail-dirty-save"
            >
              {isSaving ? "Saving..." : "Save"}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDirtyDiscard}
              data-statsig-name="routine-detail-dirty-discard"
            >
              Discard
            </Button>
            <Button
              variant="outline"
              onClick={handleDirtyCancel}
              data-statsig-name="routine-detail-dirty-cancel"
            >
              Stay
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

// --- Routines List View ---

/**
 * Nothing to show. A filtered list gets its own wording — telling someone who
 * has routines that they have none reads as a bug, not as an empty category.
 */
const RoutinesEmptyState = ({
  filter,
  isSearching,
  isNarrowedByAreaOrRole,
}: {
  filter: RoutineFilter;
  isSearching: boolean;
  isNarrowedByAreaOrRole: boolean;
}) => (
  <div className="text-center py-12 text-muted-foreground">
    <Zap className="size-8 mx-auto mb-3 opacity-50" />
    {/* A search that matched nothing is not an empty account: saying "No
        routines yet" there would tell someone with a dozen they have none. */}
    {isSearching ? (
      <div className="text-lg font-medium mb-2">No routines match your search</div>
    ) : isNarrowedByAreaOrRole ? (
      <>
        <div className="text-lg font-medium mb-2">No routines match these filters</div>
        <div className="text-sm">Pick "All" above, or change the roles under Filter.</div>
      </>
    ) : filter === "all" ? (
      <>
        <div className="text-lg font-medium mb-2">No routines yet</div>
        <div className="text-sm">
          Click "New routine" and describe one to your assistant — try "Build me a routine that
          summarizes my emails every morning."
        </div>
      </>
    ) : (
      <>
        <div className="text-lg font-medium mb-2">
          {filter === "system" ? "No system routines" : "No routines of your own"}
        </div>
        <div className="text-sm">
          {filter === "system"
            ? "None of Okthx's routines are available here."
            : 'Click "New routine" to describe one to your assistant.'}
        </div>
      </>
    )}
  </div>
);

/**
 * The routines list itself — everything below a host's header.
 *
 * Exported so the settings screen and the Tasks-style canvas view render the
 * same rows, empty states, and card/list switch. Only the header around them
 * differs: settings supplies `SettingsPanelHeader`, the canvas supplies
 * `CanvasPivotPanel`. `RoutinesPickerBody` reaches into this module for
 * `RoutineRow` on the same principle.
 */
export const RoutinesListBody = ({
  filter,
  searchQuery,
  view,
  areaRole,
  onOpen,
}: {
  filter: RoutineFilter;
  searchQuery: string;
  view: PanelViewMode;
  /** Set under `routines_v2`: the area pills and the Role filter narrow the list too. */
  areaRole?: AreaRoleFilter;
  /** Passed through to each row; see `RoutineRow`'s own `onOpen`. */
  onOpen?: (routineId: string) => void;
}) => {
  const { routines: allRoutines, isLoading } = useRoutines();
  // Under routines_v2, only the routines of installed packs, routines in no
  // pack, and routines that are on. A failed packs read shows every routine,
  // so it can never hide one.
  const routinesV2 = useRoutinesV2();
  const { packs, isLoading: packsLoading } = usePacks(routinesV2.enabled);
  // The system routines this list has shown. Kept, so turning one off does not
  // take it off the page before the user can turn it back on.
  const shownRoutineIds = useRef(new Set<string>());

  if (isLoading || packsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader />
      </div>
    );
  }

  const routines = packs
    ? routinesWithPacks(allRoutines, packs, shownRoutineIds.current)
    : allRoutines;
  // Only once both reads are in, so the full list behind the spinner is never
  // recorded. Added to during render, which is safe: adding an id again
  // changes nothing.
  for (const r of routines) if (r.systemRoutineId) shownRoutineIds.current.add(r.systemRoutineId);

  // Name and description both match: a routine's name is often a bare label
  // ("Daily Briefing") while what it actually does lives in the description.
  const query = searchQuery.trim().toLowerCase();
  const {
    narrowed: narrowedRoutines,
    visible: visibleRoutines,
    isNarrowed,
  } = filterByAreaAndRole(
    routines,
    areaRole?.selection,
    (routine) =>
      matchesRoutineFilter(routine, filter) &&
      (!query || `${routine.name} ${routine.description ?? ""}`.toLowerCase().includes(query)),
  );

  return (
    <>
      {/* Kept while the list is empty, so the pill that emptied it can be undone
          from where it was picked. Left out for an account with no routines,
          where every pill would read 0. */}
      {areaRole && routines.length > 0 ? (
        <AreaPills
          items={narrowedRoutines}
          labelItems={routines}
          filter={areaRole}
          statsigName="routines-area"
        />
      ) : null}
      {/* The empty state sits outside the grid container, not in it: as a grid
          child it lands in the first 18rem column and centers itself against
          that, not the canvas. */}
      {visibleRoutines.length === 0 ? (
        <RoutinesEmptyState
          filter={filter}
          isSearching={query.length > 0}
          // Only while the pills show: an account with no routines has nothing
          // to undo, and is told it has none.
          isNarrowedByAreaOrRole={isNarrowed && routines.length > 0}
        />
      ) : (
        <div className={view === "card" ? galleryCardsClass : cardListClass}>
          {visibleRoutines.map((routine) => (
            <RoutineRow
              key={routine.kind === "system" ? `system:${routine.id}` : routine.id}
              routine={routine}
              variant={view === "card" ? "card" : "list"}
              onOpen={onOpen}
            />
          ))}
        </div>
      )}
    </>
  );
};

/**
 * The search box and the filter/sort/view axes a routines list offers, owned in
 * one place so the settings screen and the canvas view can't drift into
 * offering different ones.
 *
 * `filter` is a parameter rather than state because its owner differs by host:
 * settings lifts it to `RoutinesFlow` (opening a routine unmounts the list, so a
 * filter held here would be back to "all" on return), while the canvas keeps the
 * list mounted and holds it locally. Search and view are always local — a search
 * is a momentary lookup, and coming back should show the list rather than the
 * last thing typed.
 */
export const useRoutinesListControls = ({
  filter,
  onFilterChange,
}: {
  filter: RoutineFilter;
  onFilterChange: (filter: RoutineFilter) => void;
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  // Cards by default: a routine is a name, a description, and a trigger, which
  // is a card's worth of content.
  const [view, setView] = useState<PanelViewMode>("card");
  const routinesV2 = useRoutinesV2();
  const areaRole = useAreaRoleFilter({
    storageKey: ROUTINES_AREA_ROLE_STORAGE_KEY,
    enabled: routinesV2.enabled,
    statsigName: "routines-role",
  });

  const kindAxis: PanelControlDimension = {
    label: "Filter",
    value: filter,
    onChange: (value: string) => {
      if (value === "all" || value === "system" || value === "custom") onFilterChange(value);
    },
    options: (["all", "system", "custom"] as const).map((option) => ({
      value: option,
      label: routineFilterLabels[option],
    })),
    defaultValue: "all",
    display: "segmented",
    statsigName: "routines-filter",
  };

  // One ordering, read by both the full row and the compact/mobile menu, so the
  // two cannot lead with different options.
  const controls: PanelControlAxes = {
    ...areaRoleFilterAxes(kindAxis, areaRole),
    view: viewAxis({
      value: view,
      onChange: setView,
      defaultValue: "card",
      statsigName: "routines-view",
    }),
  };

  return {
    searchQuery,
    view,
    controls,
    areaRole,
    search: {
      value: searchQuery,
      onChange: setSearchQuery,
      placeholder: "Search routines",
      statsigName: "routines-search",
    },
  };
};

const RoutinesList = ({
  filter,
  onFilterChange,
}: {
  /**
   * Owned by `RoutinesFlow` rather than by this component: opening a routine
   * unmounts the list, so a filter held here would be back to "all" by the time
   * the user returned to it.
   */
  filter: RoutineFilter;
  onFilterChange: (filter: RoutineFilter) => void;
}) => {
  const [, setLocation] = useLocation();
  const { searchQuery, view, controls, areaRole, search } = useRoutinesListControls({
    filter,
    onFilterChange,
  });

  /**
   * A routine is described to the assistant, not filled into a form, so this
   * hands off to a chat with the ask already in the composer. It's left unsent
   * — the user finishes the sentence before sending.
   *
   * Routed through the home page's `?input=` deep link (the one daily-brief
   * suggestion emails use) rather than opening the composer here: the new-chat
   * dialog is mounted by the calendar, and Settings is its own route.
   */
  const handleNew = useCallback(() => {
    setLocation(`/?input=${encodeURIComponent(NEW_ROUTINE_PROMPT)}`);
  }, [setLocation]);

  return (
    <div className="pb-12">
      <SettingsPanelHeader
        title="Routines"
        search={search}
        filters={
          <PanelControls labeled {...controls} subject="routines" statsigName="routines-filter" />
        }
        mobileFilters={
          <PanelControls {...controls} subject="routines" statsigName="routines-filter" />
        }
        // The trigger's label is the tool cluster's width; a change to it has to
        // re-run the header's collapse measurement.
        toolsKey={appliedFilterLabel(controls)}
        action={{
          label: "New routine",
          icon: Plus,
          onClick: handleNew,
          statsigName: "routines-new",
        }}
      />
      <p className={panelDescriptionClass}>
        Automations the assistant runs for you on a schedule or trigger.
      </p>
      <RoutinesListBody filter={filter} searchQuery={searchQuery} view={view} areaRole={areaRole} />
    </div>
  );
};

// --- New Routine Form ---

// Standalone create flow. Mirrors RoutineDetail's controlled-state layout
// so the same RoutineFormFields component drives both screens, but skips
// the threads / run / delete / reset sections — none of them make sense
// before the routine exists. New routines are created enabled by default;
// the user can flip the toggle on the detail page afterwards.
/**
 * The create flow, reached from the settings list's own route. The canvas has no
 * copy of it: a routine is described to the assistant there, so that surface
 * seeds a chat rather than opening a form.
 */
const NewRoutineForm = () => {
  const { createRoutine } = useRoutineMutations();
  const [, setLocation] = useLocation();
  const [isSaving, setIsSaving] = useState(false);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [prompt, setPrompt] = useState("");
  const [triggerConfig, setTriggerConfig] = useState<TriggerConfig>(defaultTriggerConfig("cron"));
  const [endsAt, setEndsAt] = useState<string | undefined>(undefined);
  // Seeded from the shared constant, which the agent's routines.NEW_ROUTINE uses
  // too — these had drifted, and a routine built here behaved differently from
  // the same routine built by the agent.
  const [autoSendCommunication, setAutoSendCommunication] = useState(
    NEW_ROUTINE_DEFAULT_PERMISSIONS.autoSendCommunication,
  );
  const [autoArchive, setAutoArchive] = useState(NEW_ROUTINE_DEFAULT_PERMISSIONS.autoArchive);
  const [defaultFrom, setDefaultFrom] = useState<"user" | "assistant">(
    NEW_ROUTINE_DEFAULT_PERMISSIONS.defaultFrom,
  );

  const slackTriggerAvailability = useSlackTriggerAvailability();
  const canSubmit =
    name.trim().length > 0 && prompt.trim().length > 0 && isTriggerConfigComplete(triggerConfig);

  // Guard the draft: switching to another Settings tab with typed content
  // confirms first (the create flow lives inside the Settings tab shell, so a
  // tab switch would otherwise unmount it and silently drop the draft).
  const isDirty = name.trim().length > 0 || prompt.trim().length > 0;
  const { promptingLeave, confirmLeave, cancelLeave } = useSettingsUnsavedGuard(isDirty);

  const handleTriggerTypeChange = useCallback(
    (newType: TriggerConfig["type"]) => {
      if (newType === triggerConfig.type) return;
      setTriggerConfig(defaultTriggerConfig(newType));
    },
    [triggerConfig.type],
  );

  const handleCreate = useCallback(async () => {
    if (!canSubmit) return;
    setIsSaving(true);
    try {
      const created = await createRoutine({
        name: name.trim(),
        // Optional in the form on purpose: a user writing their own routine
        // knows what it does. The requirement to supply one falls on the agent,
        // which is enforced in routines.SAVE.
        description: description.trim() || undefined,
        prompt: prompt.trim(),
        triggerConfig,
        endsAt,
        permissions: { autoSendCommunication, autoArchive, defaultFrom },
        enabled: true,
      });
      // Replace so browser-back skips the new-routine form and lands on the list.
      setLocation(`${ROUTINES_BASE_PATH}/${created.id}`, { replace: true });
    } catch (err) {
      errorToast(routineErrorMessage(err, "Failed to create routine"));
    } finally {
      setIsSaving(false);
    }
  }, [
    canSubmit,
    name,
    description,
    prompt,
    triggerConfig,
    endsAt,
    autoSendCommunication,
    autoArchive,
    defaultFrom,
    createRoutine,
    setLocation,
  ]);

  // Replace, so backing out of the create form doesn't leave it one
  // browser-back away — see the matching note in the routine editor.
  const handleCancel = useCallback(() => {
    setLocation(ROUTINES_BASE_PATH, { replace: true });
  }, [setLocation]);

  const createButton = (
    <Button
      variant="primary"
      size="sm"
      onClick={handleCreate}
      disabled={!canSubmit || isSaving}
      data-statsig-name="routine-new-create"
    >
      {isSaving ? "Creating..." : "Create"}
    </Button>
  );

  const createActions = (
    <>
      <Button
        variant="ghost"
        size="sm"
        onClick={handleCancel}
        data-statsig-name="routine-new-cancel"
      >
        Cancel
      </Button>
      {createButton}
    </>
  );

  const formFields = (
    <div className="space-y-6 max-w-2xl">
      <RoutineFormFields
        name={name}
        onNameChange={setName}
        description={description}
        onDescriptionChange={setDescription}
        prompt={prompt}
        onPromptChange={setPrompt}
        triggerConfig={triggerConfig}
        onTriggerConfigChange={setTriggerConfig}
        onTriggerTypeChange={handleTriggerTypeChange}
        slackTriggerAvailability={slackTriggerAvailability}
        endsAt={endsAt}
        onEndsAtChange={setEndsAt}
        autoSendCommunication={autoSendCommunication}
        onAutoSendCommunicationChange={setAutoSendCommunication}
        autoArchive={autoArchive}
        onAutoArchiveChange={setAutoArchive}
        defaultFrom={defaultFrom}
        onDefaultFromChange={setDefaultFrom}
        statsigPrefix="routine-new"
      />
    </div>
  );

  return (
    <>
      {/* Mobile drops Cancel: the chrome chevron already is the way out, and two
          dismiss affordances in one nav bar read as different actions. */}
      <RoutinesPanel
        title="New routine"
        onBack={handleCancel}
        actions={createActions}
        mobileActions={createButton}
      >
        {formFields}
      </RoutinesPanel>
      <Dialog open={promptingLeave} onOpenChange={(open) => !open && cancelLeave()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Discard new routine?</DialogTitle>
          </DialogHeader>
          <Separator />
          <DialogDescription>
            You have an unsaved draft. Leaving this tab will discard it.
          </DialogDescription>
          <DialogFooter>
            <Button
              variant="destructive"
              onClick={confirmLeave}
              data-statsig-name="routine-new-discard"
            >
              Discard
            </Button>
            <Button
              variant="outline"
              onClick={cancelLeave}
              data-statsig-name="routine-new-keep-editing"
            >
              Keep editing
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

// --- Routines flow (rendered inside the Settings "Routines" tab) ---

/**
 * Mode switcher for the routines flow. Reads the shared `:subId` segment of the
 * nested Settings route (`/settings/routines/:subId?`): `"new"` → create form, a
 * concrete id → detail/edit, absent → list. Settings supplies the surrounding
 * page chrome.
 */
export const RoutinesFlow = () => {
  const { subId } = useParams<{ subId?: string }>();
  // Held here, not in `RoutinesList`, so that opening a routine and coming back
  // returns to the list the user left rather than to an unfiltered one — this
  // component stays mounted across that switch, while the list does not.
  //
  // Unfiltered by default: the list is short, and someone arriving here is
  // usually looking for a routine rather than for a category of them.
  const [filter, setFilter] = useState<RoutineFilter>("all");

  if (subId === "new") {
    return <NewRoutineForm />;
  }

  if (subId) {
    return <RoutineDetail routineId={subId} />;
  }

  return <RoutinesList filter={filter} onFilterChange={setFilter} />;
};

/**
 * Routines on the canvas: the automations the assistant runs, with the same
 * search, filter, and card/list tools the settings screen offers.
 *
 * The filter is local here, unlike settings, where it is lifted to
 * `RoutinesFlow`. That indirection exists because opening a routine unmounts
 * that screen's list; this list stays mounted behind the routine's own page, so
 * it has nothing to lose.
 *
 * "New routine" hands off to the composer with the ask already in it, which is
 * what the settings list does too (BIG-7599): a routine is described to the
 * assistant rather than filled into a form.
 */
export const RoutinesCanvasView = ({
  accountSwitcher,
  leading,
  onSeedNewChat,
  openRoutineId,
  onOpenRoutine,
}: {
  accountSwitcher?: ReactNode;
  leading?: ReactNode;
  /**
   * Start a new chat with this text already in the composer. The page owns both
   * the seed and where a new chat opens, so it hands the capability down and
   * this view supplies only the sentence.
   */
  onSeedNewChat: (text: string) => void;
  /** The routine the URL names, or null for the list. */
  openRoutineId: string | null;
  /**
   * Drill into a routine, or leave one with null. The page owns the address, so
   * it navigates and hands the result back down.
   */
  onOpenRoutine: (routineId: string | null) => void;
}) => {
  const [filter, setFilter] = useState<RoutineFilter>("all");
  const { routine } = useRoutine(openRoutineId ?? "");
  const { searchQuery, view, controls, areaRole, search } = useRoutinesListControls({
    filter,
    onFilterChange: setFilter,
  });

  // Drilled in: `RoutineDetail` already knows how to render without its own
  // header bar for a host supplying chrome, which is exactly this.
  if (openRoutineId) {
    return (
      <CanvasPivotPanel
        title={routine?.name || "Routine"}
        parentLabel="Routines"
        onBack={() => onOpenRoutine(null)}
        backStatsigName="routines-canvas-back"
        leading={leading}
        accountSwitcher={accountSwitcher}
      >
        <RoutineDetail routineId={openRoutineId} embedded onLeave={() => onOpenRoutine(null)} />
      </CanvasPivotPanel>
    );
  }

  return (
    <CanvasPivotPanel
      title="Routines"
      leading={leading}
      accountSwitcher={accountSwitcher}
      toolbar={
        <PanelToolbar
          description="Automations Okthx runs for you on a schedule or trigger."
          controls={controls}
          search={{ ...search, statsigName: "routines-canvas-search" }}
          subject="routines"
          statsigName="routines-canvas-filter"
          action={{
            label: "New routine",
            icon: Plus,
            onClick: () => onSeedNewChat(NEW_ROUTINE_PROMPT),
            statsigName: "routines-canvas-new",
          }}
        />
      }
    >
      <RoutinesListBody
        filter={filter}
        searchQuery={searchQuery}
        view={view}
        areaRole={areaRole}
        onOpen={onOpenRoutine}
      />
    </CanvasPivotPanel>
  );
};
